import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type SnackbarTone = "neutral" | "positive";
export type SnackbarState = { id: number; message: string; action?: { label: string; onClick: () => void }; tone?: SnackbarTone };
export type SheetKind = "menu" | "model" | "report" | "insufficient" | "conversationMenu" | "reroll" | "plotMenu";
/** 로그인 페이지 히어로 맥락(채팅방에서 왔으면 그 플롯). 없으면 히어로는 빈 영역(기본 이미지는 추후). */
export type LoginContext = { plotName?: string; coverUrl?: string; tagline?: string };
export type PendingDeepLink = { plot: string; src?: string };

type UiState = {
  snackbar: SnackbarState | null;
  /** 열린 시트 스택(마지막이 맨 위). 시트는 라우트가 아니라 로컬 상태다. */
  sheets: SheetKind[];
  /** 채팅 입력 중 글(플롯별). 접힘/펼침·로그인 왕복에도 유지. */
  draft: Record<string, string>;
  /** 로그인 후 복귀할 경로. */
  returnTo: string | null;
  pendingDeepLink: PendingDeepLink | null;
  shownNotices: string[];
  /** 로그인 페이지 히어로 맥락. AC0-94: 로그인 요청 시트는 없고 모든 진입이 /login 페이지. */
  loginContext: LoginContext | null;
  /** 상점 진입 전 화면(부족 시트에서 왔으면 /chat/:plotId). 결제 완료 CTA가 소비한다. 메모리 전용. */
  storeReturnTo: string | null;
  setStoreReturnTo: (path: string | null) => void;
  /** tone positive = 체크 아이콘(완료 통지: 수신 동의 처리 결과·신고 완료). 기본은 아이콘 없음 */
  showSnackbar: (message: string, action?: SnackbarState["action"], tone?: SnackbarTone) => void;
  hideSnackbar: () => void;
  openSheet: (kind: SheetKind) => void;
  /** 로그인 요청: returnTo·히어로 맥락 저장. 호출부가 /login 으로 이동(가드는 redirect). */
  requestLogin: (opts?: { returnTo?: string; plot?: LoginContext }) => void;
  closeSheet: (kind?: SheetKind) => void;
  setDraft: (plotId: string, text: string) => void;
  setReturnTo: (path: string | null) => void;
  setPendingDeepLink: (link: PendingDeepLink | null) => void;
  markNoticeShown: (id: string) => void;
};

let snackbarSeq = 0;

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      snackbar: null,
      sheets: [],
      draft: {},
      returnTo: null,
      pendingDeepLink: null,
      shownNotices: [],
      loginContext: null,
      storeReturnTo: null,
      setStoreReturnTo: (storeReturnTo) => set({ storeReturnTo }),
      showSnackbar: (message, action, tone) =>
        set({ snackbar: { id: ++snackbarSeq, message, ...(action ? { action } : {}), ...(tone ? { tone } : {}) } }),
      hideSnackbar: () => set({ snackbar: null }),
      openSheet: (kind) => set((s) => (s.sheets.includes(kind) ? s : { sheets: [...s.sheets, kind] })),
      requestLogin: (opts) => set((s) => ({ returnTo: opts?.returnTo ?? s.returnTo, loginContext: opts?.plot ?? null })),
      closeSheet: (kind) => set((s) => ({ sheets: kind ? s.sheets.filter((k) => k !== kind) : s.sheets.slice(0, -1) })),
      setDraft: (plotId, text) => set((s) => ({ draft: { ...s.draft, [plotId]: text } })),
      setReturnTo: (returnTo) => set({ returnTo }),
      setPendingDeepLink: (pendingDeepLink) => set({ pendingDeepLink }),
      markNoticeShown: (id) => set((s) => (s.shownNotices.includes(id) ? s : { shownNotices: [...s.shownNotices, id] })),
    }),
    {
      name: "hamlak.ui",
      // draft·returnTo만 sessionStorage(0.3절). 나머지는 메모리.
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ draft: s.draft, returnTo: s.returnTo }),
    },
  ),
);
